import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const ivfCycle: ContentBlock<"program"> = {
  id: makeId("program-ivf-cycle"),
  kind: "program",
  tags: ["ivf", "both-partners", "treatment", "during-cycle"],
  title: "IVF Cycle",
  subtitle: "End-to-end IVF — from assessment through transfer",
  icon: "🧬",
  body: {
    duration: "4 weeks – 6 months",
    includes: [
      "Full fertility assessment for both partners",
      "Optional 90-day optimization for her and him",
      "Ovarian stimulation with daily monitoring",
      "Egg retrieval and sperm collection",
      "Fertilization and embryo culture to blastocyst",
      "Optional PGT-A genetic screening",
      "Embryo transfer with luteal phase support",
      "Two-week wait support and beta-hCG testing",
    ],
    priceRange: { low: 18000, high: 30000 },
    currency: "USD",
    status: "coming-soon",
    forWhom: "both",
    category: "treatment",
    customizable: true,
    moduleIds: [
      makeId("module-female-hormone-baseline"),
      makeId("module-male-fertility-baseline"),
      makeId("module-female-optimization"),
      makeId("module-male-optimization"),
      makeId("module-ovarian-stimulation"),
      makeId("module-egg-retrieval"),
      makeId("module-fertilization-culture"),
      makeId("module-pgt-a"),
      makeId("module-embryo-transfer"),
      makeId("module-tww-support"),
    ],
    optionalModuleIds: [
      makeId("module-female-optimization"),
      makeId("module-male-optimization"),
      makeId("module-pgt-a"),
    ],
    phases: [
      {
        title: "Assessment",
        description: "Full workup for both partners",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
      {
        title: "Optimize (optional)",
        description: "90 days of preparation for both",
        moduleIds: [
          makeId("module-female-optimization"),
          makeId("module-male-optimization"),
        ],
        parallel: true,
      },
      {
        title: "Stimulation & Retrieval",
        description: "Stimulation, retrieval, fertilization",
        moduleIds: [
          makeId("module-ovarian-stimulation"),
          makeId("module-egg-retrieval"),
          makeId("module-fertilization-culture"),
        ],
      },
      {
        title: "Screening (optional)",
        description: "PGT-A genetic testing of embryos",
        moduleIds: [makeId("module-pgt-a")],
      },
      {
        title: "Transfer & TWW",
        description: "Embryo transfer and two-week wait",
        moduleIds: [
          makeId("module-embryo-transfer"),
          makeId("module-tww-support"),
        ],
      },
    ],
    steps: [
      {
        title: "Assessment",
        description: "Full fertility workup for both partners.",
      },
      {
        title: "Optimize (optional)",
        description: "90 days of supplements and lifestyle changes.",
      },
      {
        title: "Stimulation & Retrieval",
        description:
          "10–14 days of stimulation, retrieval, fertilization, and embryo culture.",
      },
      {
        title: "Transfer",
        description:
          "Single embryo transfer with progesterone support and TWW guidance.",
      },
    ],
  },
  sources: [
    {
      title: "ART national summary report",
      journal: "CDC / SART",
      year: 2022,
      evidenceLevel: "cohort",
    },
  ],
};

registerBlock(ivfCycle);

const id = makeId("program-ivf-cycle");
registerRelationship({
  from: id,
  to: makeId("module-female-hormone-baseline"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-male-fertility-baseline"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-female-optimization"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-male-optimization"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-ovarian-stimulation"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-egg-retrieval"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-fertilization-culture"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-pgt-a"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-embryo-transfer"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-tww-support"),
  type: "contains",
});
